import { defineStore } from 'pinia'
import { useCookie } from '@core/composable/useCookie'
import { ability } from '@/plugins/casl/ability'
import { railsApi } from '@/utils/railsApi'

const ADMIN_ABILITY_RULES = [{ action: 'manage', subject: 'all' }]

function setSignedInFlag(value) {
  useCookie('adminSignedIn').value = value ? '1' : null
}

function applyAdminAbilities() {
  useCookie('userAbilityRules').value = ADMIN_ABILITY_RULES
  ability.update(ADMIN_ABILITY_RULES)
}

function clearAdminAbilities() {
  useCookie('userAbilityRules').value = null
  ability.update([])
}

export const usePlatformAdminAccountStore = defineStore('platformAdminAccount', {
  state: () => ({
    platformAdmin: null,
    loading: false,
    error: null,
    initialized: false,
  }),

  getters: {
    isSignedIn: state => !!state.platformAdmin,
  },

  actions: {
    async bootstrap() {
      if (this.initialized)
        return this.platformAdmin

      this.loading = true
      this.error = null
      try {
        if (!useCookie('adminSignedIn').value) {
          this.platformAdmin = null

          return null
        }
        const data = await railsApi('/api/v1/admin/me')

        this.platformAdmin = data.platform_admin
        setSignedInFlag(true)
        applyAdminAbilities()

        return this.platformAdmin
      }
      catch (err) {
        if (err.status === 401) {
          this.platformAdmin = null
          setSignedInFlag(false)
          clearAdminAbilities()

          return null
        }
        this.error = err.message

        return null
      }
      finally {
        this.loading = false
        this.initialized = true
      }
    },

    async login({ email, password }) {
      this.loading = true
      this.error = null
      try {
        const data = await railsApi('/api/v1/admin/session', {
          method: 'POST',
          body: { platform_admin: { email, password } },
        })

        this.platformAdmin = data.platform_admin
        this.initialized = true
        setSignedInFlag(true)
        applyAdminAbilities()

        return data.platform_admin
      }
      catch (err) {
        this.error = err.payload?.error || err.message
        throw err
      }
      finally {
        this.loading = false
      }
    },

    async logout() {
      this.loading = true
      this.error = null
      try {
        await railsApi('/api/v1/admin/session', { method: 'DELETE' })
      }
      catch (_) {
        // Idempotent
      }
      finally {
        const { clearPwaStateOnLogout } = await import('@/utils/pwaCache')

        await clearPwaStateOnLogout()
        this.platformAdmin = null
        this.initialized = true
        setSignedInFlag(false)
        clearAdminAbilities()
        this.loading = false
      }
    },
  },
})
